import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  TextField,
} from "@mui/material";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { getFunctions, httpsCallable } from "firebase/functions";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { doc } from "firebase/firestore";


const ConsultationSection = ({ title, subtitle, description }) => {
  const sectionRef = useRef(null)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async(e) => {
    e.preventDefault();

    // Handle form submission logic here
    try {
      setIsSubmitting(true);
      const functions = getFunctions();
      const contactSupport = httpsCallable(functions, "contactSupport");

      const message = `
        Phone: ${formData.phone}
      `;

      await contactSupport({
        name: formData.name,
        email: formData.email,
        message,
      });

      toast.success("Consultation request sent");

      setFormData({
        name: "",
        email: "",
        phone: "",
      });
    } catch (error) {
      setIsSubmitting(false);
      toast.error("Error submitting consultation form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const location = useLocation();

useEffect(() => {
  if (location.state?.scrollTo === "book-demo") {
    setTimeout(() => {
      const el = document.getElementById("book-demo");
      el?.scrollIntoView({ behavior: "smooth" });
    }, 100);

    // Clear state so it doesn't scroll again on refresh
    window.history.replaceState({}, document.title);
  }
}, [location.state]);



  return (
    <Box ref={sectionRef} id="book-demo" sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          {/* Left Side - Consultant Image */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: "relative",
                borderRadius: "20px",
                overflow: "hidden",
                height: { xs: "400px", md: "500px" },
              }}
            >
              <Box
                component="img"
                src={require("assets/images/consultant.png")}
                alt="Consultation"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "20px",
                }}
              />
            </Box>
          </Grid>

          {/* Right Side - Consultation Form */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                backgroundColor: "rgba(45, 45, 45, 0.8)",
                borderRadius: "20px",
                padding: { xs: 3, md: 4 },
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
              }}
            >
              {/* Header */}
              <Typography
                variant="h3"
                sx={{
                  color: "#17c1e8",
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  fontWeight: "bold",
                  mb: 2,
                  fontFamily: "Outfit",
                }}
              >
                {title || "Get a Free Consultation!"}
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  mb: 4,
                  fontFamily: "Outfit",
                }}
              >
                {description ||
                  "Get a personalized diet plan tailored to your health goals free for a limited time!"}
              </Typography>

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Name Field */}
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        mb: 1,
                        fontFamily: "Outfit",
                      }}
                    >
                      Name*
                    </Typography>
                    <TextField
                      fullWidth
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: "10px",
                          "& fieldset": {
                            borderColor: "transparent",
                          },
                          "&:hover fieldset": {
                            borderColor: "#17c1e8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#17c1e8",
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#242424",
                          fontFamily: "Outfit",
                          "&::placeholder": {
                            color: "#666",
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Email Field */}
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        mb: 1,
                        fontFamily: "Outfit",
                      }}
                    >
                      Mail ID*
                    </Typography>
                    <TextField
                      fullWidth
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="E-mail ID"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: "10px",
                          "& fieldset": {
                            borderColor: "transparent",
                          },
                          "&:hover fieldset": {
                            borderColor: "#17c1e8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#17c1e8",
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#242424",
                          fontFamily: "Outfit",
                          "&::placeholder": {
                            color: "#666",
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Phone Field */}
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        mb: 1,
                        fontFamily: "Outfit",
                      }}
                    >
                      Phone No*
                    </Typography>
                    <TextField
                      fullWidth
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Mobile Number"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: "10px",
                          "& fieldset": {
                            borderColor: "transparent",
                          },
                          "&:hover fieldset": {
                            borderColor: "#17c1e8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#17c1e8",
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#242424",
                          fontFamily: "Outfit",
                          "&::placeholder": {
                            color: "#666",
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      fullWidth
                      sx={{
                        backgroundColor: "#17c1e8",
                        color: "#242424",
                        padding: { xs: "12px 24px", md: "16px 32px" },
                        borderRadius: "10px",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        fontWeight: 400,
                        textTransform: "none",
                        fontFamily: "Outfit",
                        mt: 2,
                        "&:hover": {
                          backgroundColor: "#00bcd4",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      { isSubmitting ? 'Submitting...' : 'Submit' }
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

ConsultationSection.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
};

export default ConsultationSection;
